import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, ShareIcon, ArrowRightIcon, HomeIcon, Image as ImageIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
/**
 * Canonical home icon used across the whole app.
 * Icon-only (no circular background) — maroon on light, orange on dark.
 * Matches the SendMoneyScreen baseline and the uploaded reference images.
 */
export function HomeIconButton({
  onNavigateHome,
  className = '',
  size = 20
}: {
  onNavigateHome?: () => void;
  className?: string;
  size?: number;
}) {
  const {
    isDark
  } = useTheme();
  return <button onClick={onNavigateHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'} ${className}`} aria-label="Home" data-id="element-3625" data-mp="guoblmfrgjvg">
      <HomeIcon style={{
      width: size,
      height: size
    }} strokeWidth={2} data-id="element-3626" data-mp="2b7eakikzkj9p" />
    </button>;
}
export interface ReceiptInfoRow {
  label: string;
  value: string;
}
interface TransactionReceiptProps {
  amount: number;
  /** Pre-formatted date string, e.g. "06-May-2026" */
  dateLabel?: string;
  /** Pre-formatted time string, e.g. "03:00:18 PM" */
  timeLabel?: string;
  refNumber?: string;
  toName: string;
  toAccount?: string;
  fromName: string;
  fromAccount?: string;
  additionalInfo?: ReceiptInfoRow[];
  onNavigateHome?: () => void;
  onShare?: () => void;
  onSaveImage?: () => void;
}
/**
 * Canonical transaction receipt — the SendMoneyScreen glass receipt.
 * 357x257 glass card, 15px radius, 1.5px orange border, green success indicator,
 * To/From card, collapsible Additional Information, notification card, digiBOP logo,
 * and Share / Save Image / Logout actions.
 */
export function TransactionReceipt({
  amount,
  dateLabel,
  timeLabel,
  refNumber = '78343846823',
  toName,
  toAccount,
  fromName,
  fromAccount,
  additionalInfo = [],
  onNavigateHome,
  onShare,
  onSaveImage
}: TransactionReceiptProps) {
  const {
    isDark
  } = useTheme();
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(true);
  const now = new Date();
  const formattedDate = dateLabel ?? now.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).replace(/ /g, '-');
  const formattedTime = timeLabel ?? now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
  const maskAccount = (acct?: string) => {
    if (!acct) return '*********1234';
    const digits = acct.replace(/[^\d]/g, '');
    return '*********' + (digits.slice(-4) || acct.slice(-4));
  };
  return <motion.div key="success" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="w-full min-h-screen flex flex-col relative overflow-hidden" style={{
    background: isDark ? '#1A0E08' : '#F2F2F2'
  }} data-id="element-3627" data-mp="5a6r8gutiw8a">
      {!isDark && <div aria-hidden className="absolute pointer-events-none" style={{
      top: '-5%',
      left: '-15%',
      width: '130%',
      height: '52%',
      background: 'radial-gradient(ellipse 60% 55% at 52% 42%, rgba(243,130,50,0.38) 0%, rgba(243,150,80,0.20) 38%, rgba(243,112,36,0.06) 65%, transparent 80%)',
      filter: 'blur(22px)',
      zIndex: 0
    }} data-id="element-3628" data-mp="3hcomur43eupj" />}
      {isDark && <div aria-hidden className="absolute pointer-events-none" style={{
      top: '-5%',
      left: '-15%',
      width: '130%',
      height: '52%',
      background: 'radial-gradient(ellipse 60% 55% at 52% 42%, rgba(243,112,36,0.18) 0%, transparent 70%)',
      filter: 'blur(22px)',
      zIndex: 0
    }} data-id="element-3629" data-mp="33lt6yuv6fdcg" />}
      <div className="px-5 pt-6 pb-4 flex items-center justify-between relative z-10" data-id="element-3630" data-mp="2kspf9f1irakh">
        <h1 className={`text-[18px] font-regular ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`} data-id="element-3631" data-mp="qcquqdnwxern">
          Receipt
        </h1>
        <button onClick={onNavigateHome} className={`p-2 -mr-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} aria-label="Home" data-id="element-3632" data-mp="104wn3oi2ty7d">
          <HomeIcon className="w-6 h-6" strokeWidth={1.8} data-id="element-3633" data-mp="195ben5lmw9jb" />
        </button>
      </div>
      <div className="px-5 flex-1 flex flex-col pb-6 space-y-4 relative z-10" data-id="element-3634" data-mp="1975yshp2zxmc">
        {/* Glass success card */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} className="flex flex-col items-center relative overflow-hidden mx-auto" style={{
        width: '357px',
        maxWidth: '100%',
        height: '257px',
        borderRadius: '15px',
        background: isDark ? 'rgba(45,24,16,0.82)' : 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1.5px solid #F37024',
        boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)' : '0 8px 32px rgba(243,112,36,0.10), inset 0 1px 0 rgba(255,255,255,0.9)',
        padding: '28px 24px',
        justifyContent: 'center'
      }} data-id="element-3635" data-mp="1j38zdj4abo76">
          <div className="w-14 h-14 rounded-full bg-[#22C55E] flex items-center justify-center mb-3 z-10" style={{
          boxShadow: '0 4px 12px rgba(34,197,94,0.35)'
        }} data-id="element-3636" data-mp="12pkv3ezlh76e">
            <CheckIcon className="w-7 h-7 text-white" strokeWidth={3} data-id="element-3637" data-mp="2x4drvhc7s3zx" />
          </div>
          <p className="text-[15px] font-medium mb-5 z-10" style={{
          color: isDark ? '#C4B5AA' : '#5A5B5E'
        }} data-id="element-3638" data-mp="32145zz4gkpip">
            Success
          </p>
          <div className="flex items-baseline justify-center gap-2 mb-5 z-10" data-id="element-3639" data-mp="2vkvjyu55ycpf">
            <span className="text-[22px] font-medium" style={{
            color: isDark ? '#B8A89E' : '#9E9E9E'
          }} data-id="element-3640" data-mp="2qmann5t18d69">
              PKR
            </span>
            <span className="font-bold leading-none" style={{
            fontSize: '52px',
            color: isDark ? '#FFFFFF' : '#5A1408',
            letterSpacing: '-1.5px'
          }} data-id="element-3641" data-mp="3jag4vikvnk1c">
              {amount.toLocaleString('en-US')}
            </span>
          </div>
          <p className="text-[13px] font-medium z-10 mb-1" style={{
          color: isDark ? '#C4B5AA' : '#303030'
        }} data-id="element-3642" data-mp="35jun5x75tg51">
            {formattedDate}, {formattedTime}
          </p>
          <p className="text-[13px] font-medium z-10" style={{
          color: isDark ? '#C4B5AA' : '#303030'
        }} data-id="element-3643" data-mp="1jsmlv3b0uky0">
            Ref# {refNumber}
          </p>
        </motion.div>

        {/* To / From card */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} className="rounded-2xl px-5 py-5" style={{
        background: isDark ? '#2D1810' : '#FFFFFF',
        border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #E8E8E8',
        boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.04)'
      }} data-id="element-3644" data-mp="1srztrrjhz1gm">
          <div className="flex items-start justify-between mb-5" data-id="element-3645" data-mp="2qgtgfv0birpg">
            <span className="text-[14px] font-bold mt-0.5" style={{
            color: '#5A1408'
          }} data-id="element-3646" data-mp="2qeyuqeh82vkb">
              To:
            </span>
            <div className="text-right" data-id="element-3647" data-mp="1wekvh4f363m8">
              <p className="text-[14px] font-medium" style={{
              color: isDark ? '#FFFFFF' : '#1A1A1A'
            }} data-id="element-3648" data-mp="2af45gh343jr1">
                {toName}
              </p>
              <p className="text-[13px] mt-0.5" style={{
              color: isDark ? '#B8A89E' : '#5A5B5E'
            }} data-id="element-3649" data-mp="1io7qnaxhr514">
                {maskAccount(toAccount)}
              </p>
            </div>
          </div>
          <div className="h-px w-full mb-5" style={{
          background: isDark ? 'rgba(243,112,36,0.1)' : '#F0F0F0'
        }} data-id="element-3650" data-mp="1mohv2j5mxndv" />
          <div className="flex items-start justify-between" data-id="element-3651" data-mp="uxtx1fz9cy4f">
            <span className="text-[14px] font-bold mt-0.5" style={{
            color: '#5A1408'
          }} data-id="element-3652" data-mp="uxc2cns8urym">
              From:
            </span>
            <div className="text-right" data-id="element-3653" data-mp="pm6kr75igjb4">
              <p className="text-[14px] font-medium" style={{
              color: isDark ? '#FFFFFF' : '#1A1A1A'
            }} data-id="element-3654" data-mp="avqx7lpmgz9s">
                {fromName}
              </p>
              <p className="text-[13px] mt-0.5" style={{
              color: isDark ? '#B8A89E' : '#5A5B5E'
            }} data-id="element-3655" data-mp="13lg2wcda1nhq">
                {maskAccount(fromAccount)}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Additional Information */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.15
      }} className="rounded-2xl px-5 py-4" style={{
        background: isDark ? '#2D1810' : '#FFFFFF',
        border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #E8E8E8',
        boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.04)'
      }} data-id="element-3656" data-mp="yjgzx3lg8slb">
          <button onClick={() => setShowAdditionalInfo(!showAdditionalInfo)} className="w-full flex items-center justify-between mb-1" data-id="element-3657" data-mp="afkgvd3tho52">
            <span className="text-[14px] font-medium" style={{
            color: isDark ? '#FFFFFF' : '#1A1A1A'
          }} data-id="element-3658" data-mp="fc15e8exi4io">
              Additional Information
            </span>
            <ChevronDownIcon className={`w-5 h-5 transition-transform ${showAdditionalInfo ? 'rotate-180' : ''}`} style={{
            color: isDark ? '#B8A89E' : '#1A1A1A'
          }} data-id="element-3659" data-mp="1uumidbk1y1xg" />
          </button>
          <AnimatePresence initial={false} data-id="element-3660" data-mp="3ka6x71jj4zvf">
            {showAdditionalInfo && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.25
          }} className="overflow-hidden" data-id="element-3661" data-mp="3fernmlsk1q4t">
                <div className="pt-4 space-y-4" data-id="element-3662" data-mp="gu88csgssi0h">
                  {additionalInfo.map(item => <div key={item.label} className="flex justify-between items-center" data-id="element-3663" data-mp="bxrj1uxv3xlt">
                      <span className="text-[13px] font-bold" style={{
                  color: '#F37024'
                }} data-id="element-3664" data-mp="2do2y39e5neg3">
                        {item.label}
                      </span>
                      <span className="text-[13px] font-medium" style={{
                  color: isDark ? '#FFFFFF' : '#1A1A1A'
                }} data-id="element-3665" data-mp="mlm9rdsjt5gn">
                        {item.value}
                      </span>
                    </div>)}
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>

        {/* Notification card */}
        <div className="rounded-xl px-5 py-4 flex items-center gap-3" style={{
        background: isDark ? '#2D1810' : '#FFFFFF',
        border: isDark ? '1px solid rgba(243,112,36,0.18)' : '1px solid #E8E8E8',
        boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.04)'
      }} data-id="element-3666" data-mp="1abgk9opeahxq">
          <div className="w-10 h-10 rounded-full bg-[#F37024]/10 flex items-center justify-center shrink-0" data-id="element-3667" data-mp="1tdyw2j7r41hy">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-3668" data-mp="1oemas2wj32hh">
              <path d="M18 8A6 6 0 0 0 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="#F37024" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3669" data-mp="2h3jr40h6ip7l" />
              <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="#F37024" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3670" data-mp="27cfbcyjoj0bp" />
            </svg>
          </div>
          <p className="text-[12px]" style={{
          color: isDark ? '#B8A89E' : '#5A5B5E'
        }} data-id="element-3671" data-mp="2fne3h3iatywu">
            Notification sent to your registered Mobile/Email
          </p>
        </div>

        <div className="flex-1 min-h-[12px]" data-id="element-3672" data-mp="2aloppg3oz752" />
        <div className="flex justify-center mb-2" data-id="element-3673" data-mp="2amqawdef9a0i">
          <img src="/Dark_theme_logo-removebg-preview.png" alt="digiBOP" className="h-10 object-contain" style={{
          filter: isDark ? 'brightness(0) invert(1)' : 'none'
        }} data-id="element-3674" data-mp="2ybaow3k2dmd2" />
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} className="flex items-start justify-around pt-1 pb-2" data-id="element-3675" data-mp="2pho75hcoyjmv">
          <button onClick={onShare} className="flex flex-col items-center gap-1.5 px-4 active:scale-95 transition-transform" data-id="element-3676" data-mp="1alwgo3a4tb2y">
            <ShareIcon style={{
            width: 26,
            height: 26,
            color: isDark ? '#F5CBA7' : '#5A1408'
          }} strokeWidth={1.8} data-id="element-3677" data-mp="15lh1r7a7bviv" />
            <span className="text-[12px] font-medium" style={{
            color: isDark ? '#F5CBA7' : '#5A1408'
          }} data-id="element-3678" data-mp="rnabac7l0xt8">
              Share
            </span>
          </button>
          <button onClick={onSaveImage} className="flex flex-col items-center gap-1.5 px-4 active:scale-95 transition-transform" data-id="element-3679" data-mp="11mrdgjnmktkg">
            <ImageIcon style={{
            width: 26,
            height: 26,
            color: isDark ? '#F5CBA7' : '#5A1408'
          }} strokeWidth={1.8} data-id="element-3680" data-mp="2d0mwi3hnhmgy" />
            <span className="text-[12px] font-medium" style={{
            color: isDark ? '#F5CBA7' : '#5A1408'
          }} data-id="element-3681" data-mp="2f5rtw1rtptb7">
              Save Image
            </span>
          </button>
          <button onClick={onNavigateHome} className="flex flex-col items-center gap-1.5 px-4 active:scale-95 transition-transform" data-id="element-3682" data-mp="2p3xumg1w8teh">
            <ArrowRightIcon style={{
            width: 26,
            height: 26,
            color: isDark ? '#F5CBA7' : '#5A1408'
          }} strokeWidth={1.8} data-id="element-3683" data-mp="1wfjvfwz2jr1c" />
            <span className="text-[12px] font-medium" style={{
            color: isDark ? '#F5CBA7' : '#5A1408'
          }} data-id="element-3684" data-mp="26b3o8aic69lv">
              Logout
            </span>
          </button>
        </motion.div>
      </div>
    </motion.div>;
}